import { useEffect, useMemo, useState } from 'react';
import './AccountPage.css';
import { fetchAccount, loginAccount, logoutAccount, registerAccount } from './contentApi';

const EMPTY_SIGN_IN = { email: '', password: '', remember: true };
const EMPTY_REGISTER = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  newsletter: true,
  terms: false,
};

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

function passwordScore(value) {
  let score = 0;
  if (value.length >= 8) score += 1;
  if (/[A-Z]/.test(value)) score += 1;
  if (/[0-9]/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  return score;
}

function validateSignIn(values) {
  const errors = {};
  if (!values.email.trim()) errors.email = 'Please enter your email address.';
  else if (!isEmail(values.email)) errors.email = 'Please enter a valid email address.';
  if (!values.password) errors.password = 'Please enter your password.';
  return errors;
}

function validateRegister(values) {
  const errors = {};
  if (!values.firstName.trim()) errors.firstName = 'Please enter your first name.';
  if (!values.lastName.trim()) errors.lastName = 'Please enter your last name.';
  if (!values.email.trim()) errors.email = 'Please enter your email address.';
  else if (!isEmail(values.email)) errors.email = 'Please enter a valid email address.';
  if (values.phone.trim() && !/^[+0-9\s()-]{6,20}$/.test(values.phone.trim())) {
    errors.phone = 'Please enter a valid phone number.';
  }
  if (!values.password) errors.password = 'Please choose a password.';
  else if (passwordScore(values.password) < 3) {
    errors.password = 'Use at least 8 characters with an uppercase letter and a number.';
  }
  if (values.confirmPassword !== values.password) errors.confirmPassword = 'The two passwords do not match.';
  if (!values.terms) errors.terms = 'Please accept the terms to create your account.';
  return errors;
}

function Field({ id, label, error, children, hint }) {
  return (
    <div className={`account-field${error ? ' account-field-invalid' : ''}`}>
      <label htmlFor={id}>{label}</label>
      {children}
      {error
        ? <p className="account-field-error" id={`${id}-error`} role="alert">{error}</p>
        : hint && <p className="account-field-hint">{hint}</p>}
    </div>
  );
}

function PasswordInput({ id, value, onChange, error, autoComplete, placeholder }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="account-password">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      <button type="button" onClick={() => setVisible((current) => !current)}
        aria-label={visible ? 'Hide password' : 'Show password'} aria-pressed={visible}>
        {visible ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

function PasswordMeter({ value }) {
  const score = passwordScore(value);
  const labels = ['Too weak', 'Weak', 'Fair', 'Strong', 'Excellent'];

  return (
    <div className="account-meter" aria-hidden="true">
      <div className="account-meter-track">
        {[0, 1, 2, 3].map((index) => (
          <span key={index} className={index < score ? `account-meter-on account-meter-${score}` : ''} />
        ))}
      </div>
      <span className="account-meter-label">{value ? labels[score] : 'At least 8 characters'}</span>
    </div>
  );
}

function AccountDashboard({ account, onSignOut }) {
  const initials = `${account.firstName?.[0] || account.email[0]}${account.lastName?.[0] || ''}`.toUpperCase();

  return (
    <div className="account-dashboard">
      <div className="account-avatar" aria-hidden="true">{initials}</div>
      <p className="account-eyebrow">My account</p>
      <h1>Welcome{account.firstName ? `, ${account.firstName}` : ' back'}</h1>
      <p className="account-dashboard-mail">{account.email}</p>

      <div className="account-dashboard-grid">
        <a href="/the-deco-boutique"><span aria-hidden="true">✦</span>New collection</a>
        <a href="/spring-summer"><span aria-hidden="true">❖</span>Spring-Summer</a>
        <a href="/outdoor-collection"><span aria-hidden="true">❍</span>Outdoor</a>
        <a href="tel:+8801337018777"><span aria-hidden="true">☎</span>Contact an advisor</a>
      </div>

      <button className="account-submit account-signout" type="button" onClick={onSignOut}>Sign out</button>
    </div>
  );
}

function AccountPage({ mode = 'login', onNavigateHome }) {
  const [tab, setTab] = useState(mode === 'register' ? 'register' : 'login');
  const [account, setAccount] = useState(null);
  const [signIn, setSignIn] = useState(EMPTY_SIGN_IN);
  const [register, setRegister] = useState(EMPTY_REGISTER);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setTab(mode === 'register' ? 'register' : 'login');
  }, [mode]);

  // Restore the session from the stored API token, if there is one.
  useEffect(() => {
    let active = true;
    fetchAccount()
      .then((stored) => { if (active && stored) setAccount(stored); })
      .catch(() => { /* offline: stay signed out */ });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.title = account
      ? 'My account | MONALISA LIVING'
      : `${tab === 'register' ? 'Create an account' : 'Sign in'} | MONALISA LIVING`;
  }, [tab, account]);

  const switchTab = (next) => {
    setTab(next);
    setErrors({});
    setStatus(null);
  };

  const submitSignIn = async (event) => {
    event.preventDefault();
    const found = validateSignIn(signIn);
    setErrors(found);
    if (Object.keys(found).length) return;

    setPending(true);
    setStatus(null);

    try {
      const signed = await loginAccount(signIn.email.trim(), signIn.password);
      setAccount(signed);
      setSignIn(EMPTY_SIGN_IN);
      setStatus({ type: 'success', text: 'You are signed in.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message });
    } finally {
      setPending(false);
    }
  };

  const submitRegister = async (event) => {
    event.preventDefault();
    const found = validateRegister(register);
    setErrors(found);
    if (Object.keys(found).length) return;

    setPending(true);
    setStatus(null);

    try {
      const created = await registerAccount({
        firstName: register.firstName.trim(),
        lastName: register.lastName.trim(),
        email: register.email.trim(),
        phone: register.phone.trim(),
        password: register.password,
        newsletter: register.newsletter,
      });
      setAccount(created);
      setRegister(EMPTY_REGISTER);
      setStatus({ type: 'success', text: 'Your account has been created.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message });
    } finally {
      setPending(false);
    }
  };

  const signOut = async () => {
    await logoutAccount();
    setAccount(null);
    setSignIn(EMPTY_SIGN_IN);
    setStatus({ type: 'success', text: 'You have been signed out.' });
    setTab('login');
  };

  const benefits = useMemo(() => ([
    'Save your favourite pieces across every device',
    'Book a private appointment in your nearest showroom',
    'Follow your orders and bespoke commissions',
    'Preview each new collection before it is published',
  ]), []);

  return (
    <main id="maincontent" className="account-page">
      <section className="account-aside" aria-label="MONALISA LIVING membership"
        style={{ '--account-aside-image': `url("${process.env.PUBLIC_URL}/deco-tokonatsu.webp")` }}>
        <div className="account-aside-inner">
          <p className="account-eyebrow">MONALISA LIVING</p>
          <h2>The house of considered interiors</h2>
          <p className="account-aside-text">
            Create your account to keep every selection, appointment and commission in one place —
            and let our advisors design around the way you live.
          </p>
          <ul className="account-benefits">
            {benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}
          </ul>
          <a className="account-aside-link" href="tel:+8801337018777">Speak to an advisor · +880 1337 018777</a>
        </div>
      </section>

      <section className="account-panel" aria-label={account ? 'My account' : 'Sign in or create an account'}>
        <div className="account-panel-inner">
          {account ? (
            <AccountDashboard account={account} onSignOut={signOut} />
          ) : (
            <>
              <p className="account-eyebrow">My account</p>
              <h1>{tab === 'register' ? 'Create your account' : 'Welcome back'}</h1>
              <p className="account-lede">
                {tab === 'register'
                  ? 'A few details and your selections travel with you.'
                  : 'Sign in to find your selections, appointments and orders.'}
              </p>

              <div className="account-tabs" role="tablist" aria-label="Account access">
                <button role="tab" type="button" id="account-tab-login"
                  aria-selected={tab === 'login'} aria-controls="account-panel-login"
                  className={tab === 'login' ? 'is-active' : ''} onClick={() => switchTab('login')}>
                  Sign in
                </button>
                <button role="tab" type="button" id="account-tab-register"
                  aria-selected={tab === 'register'} aria-controls="account-panel-register"
                  className={tab === 'register' ? 'is-active' : ''} onClick={() => switchTab('register')}>
                  Create an account
                </button>
              </div>

              {status && (
                <p className={`account-status account-status-${status.type}`} role="status">{status.text}</p>
              )}

              {tab === 'login' ? (
                <form className="account-form" id="account-panel-login" role="tabpanel"
                  aria-labelledby="account-tab-login" onSubmit={submitSignIn} noValidate>
                  <Field id="signin-email" label="Email address" error={errors.email}>
                    <input id="signin-email" type="email" autoComplete="email" placeholder="you@example.com"
                      value={signIn.email} aria-invalid={errors.email ? 'true' : undefined}
                      aria-describedby={errors.email ? 'signin-email-error' : undefined}
                      onChange={(event) => setSignIn({ ...signIn, email: event.target.value })} />
                  </Field>

                  <Field id="signin-password" label="Password" error={errors.password}>
                    <PasswordInput id="signin-password" autoComplete="current-password" placeholder="••••••••"
                      value={signIn.password} error={errors.password}
                      onChange={(event) => setSignIn({ ...signIn, password: event.target.value })} />
                  </Field>

                  <div className="account-row">
                    <label className="account-check">
                      <input type="checkbox" checked={signIn.remember}
                        onChange={(event) => setSignIn({ ...signIn, remember: event.target.checked })} />
                      <span>Keep me signed in</span>
                    </label>
                    <button className="account-link" type="button"
                      onClick={() => setStatus({ type: 'info', text: 'A reset link would be sent to your email address.' })}>
                      Forgot your password?
                    </button>
                  </div>

                  <button className="account-submit" type="submit" disabled={pending}>
                    {pending ? 'Signing in…' : 'Sign in'}
                  </button>

                  <p className="account-switch">
                    New to MONALISA LIVING?{' '}
                    <button className="account-link" type="button" onClick={() => switchTab('register')}>
                      Create an account
                    </button>
                  </p>
                </form>
              ) : (
                <form className="account-form" id="account-panel-register" role="tabpanel"
                  aria-labelledby="account-tab-register" onSubmit={submitRegister} noValidate>
                  <div className="account-field-pair">
                    <Field id="register-first" label="First name" error={errors.firstName}>
                      <input id="register-first" type="text" autoComplete="given-name" value={register.firstName}
                        aria-invalid={errors.firstName ? 'true' : undefined}
                        aria-describedby={errors.firstName ? 'register-first-error' : undefined}
                        onChange={(event) => setRegister({ ...register, firstName: event.target.value })} />
                    </Field>
                    <Field id="register-last" label="Last name" error={errors.lastName}>
                      <input id="register-last" type="text" autoComplete="family-name" value={register.lastName}
                        aria-invalid={errors.lastName ? 'true' : undefined}
                        aria-describedby={errors.lastName ? 'register-last-error' : undefined}
                        onChange={(event) => setRegister({ ...register, lastName: event.target.value })} />
                    </Field>
                  </div>

                  <Field id="register-email" label="Email address" error={errors.email}>
                    <input id="register-email" type="email" autoComplete="email" placeholder="you@example.com"
                      value={register.email} aria-invalid={errors.email ? 'true' : undefined}
                      aria-describedby={errors.email ? 'register-email-error' : undefined}
                      onChange={(event) => setRegister({ ...register, email: event.target.value })} />
                  </Field>

                  <Field id="register-phone" label="Phone number (optional)" error={errors.phone}
                    hint="So an advisor can reach you about appointments.">
                    <input id="register-phone" type="tel" autoComplete="tel" placeholder="+880 1337 018777"
                      value={register.phone} aria-invalid={errors.phone ? 'true' : undefined}
                      aria-describedby={errors.phone ? 'register-phone-error' : undefined}
                      onChange={(event) => setRegister({ ...register, phone: event.target.value })} />
                  </Field>

                  <Field id="register-password" label="Password" error={errors.password}>
                    <PasswordInput id="register-password" autoComplete="new-password" placeholder="••••••••"
                      value={register.password} error={errors.password}
                      onChange={(event) => setRegister({ ...register, password: event.target.value })} />
                    <PasswordMeter value={register.password} />
                  </Field>

                  <Field id="register-confirm" label="Confirm password" error={errors.confirmPassword}>
                    <PasswordInput id="register-confirm" autoComplete="new-password" placeholder="••••••••"
                      value={register.confirmPassword} error={errors.confirmPassword}
                      onChange={(event) => setRegister({ ...register, confirmPassword: event.target.value })} />
                  </Field>

                  <label className="account-check">
                    <input type="checkbox" checked={register.newsletter}
                      onChange={(event) => setRegister({ ...register, newsletter: event.target.checked })} />
                    <span>Send me collection previews and showroom invitations.</span>
                  </label>

                  <div className={errors.terms ? 'account-field account-field-invalid' : 'account-field'}>
                    <label className="account-check">
                      <input type="checkbox" checked={register.terms}
                        onChange={(event) => setRegister({ ...register, terms: event.target.checked })} />
                      <span>I accept the terms of use and the privacy policy.</span>
                    </label>
                    {errors.terms && <p className="account-field-error" role="alert">{errors.terms}</p>}
                  </div>

                  <button className="account-submit" type="submit" disabled={pending}>
                    {pending ? 'Creating your account…' : 'Create my account'}
                  </button>

                  <p className="account-switch">
                    Already have an account?{' '}
                    <button className="account-link" type="button" onClick={() => switchTab('login')}>Sign in</button>
                  </p>
                </form>
              )}
            </>
          )}

          <button className="account-back" type="button" onClick={onNavigateHome}>← Back to the home page</button>
        </div>
      </section>
    </main>
  );
}

export default AccountPage;
